import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import SuitcaseIcon from '@atlaskit/icon/core/briefcase';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import {
	ActionsSection,
	AgentKnowledgeSection,
	getEnabledKnowlegeArisFromAgent,
	useKnowledgeProductsRender,
} from '@atlassian/conversation-assistant-agent';
import {
	type AgentDetails,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service';
import { AnalyticsEventSource } from '@atlassian/ptc-common';

import { trimUserARI } from '../../hooks/utils';
import ProfileAboutTeams from '../ProfileAboutTeams';

const agentAboutPanelStyles = xcss({
	boxShadow: 'elevation.shadow.raised',
	borderRadius: '6px',
	padding: 'space.300',
	display: 'flex',
	flexDirection: 'column',
});

const headingStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',
	font: 'font.heading.xxsmall',
});

const AbouSectionHeaders = ({ children }: { children: React.ReactNode }) => (
	<Box xcss={headingStyles}>{children}</Box>
);

export const AgentAboutPanelWrapper = ({ children }: { children: React.ReactNode }) => (
	<Stack xcss={agentAboutPanelStyles} space="space.300">
		{children}
	</Stack>
);

export const AgentAboutPanel = ({
	agent,
	assistanceServiceParams,
}: { agent: AgentDetails } & AssistanceServiceInputParam) => {
	const { formatMessage } = useIntl();
	const agentIdentityId = trimUserARI(agent?.identity_account_id || '');

	const { getKnowledgeProductsRenderForAgent } = useKnowledgeProductsRender({
		assistanceServiceParams,
	});

	const isForgeAgent = agent.creator_type === 'THIRD_PARTY' || agent.creator_type === 'FORGE';

	return (
		<>
			<AgentAboutPanelWrapper>
				<Stack space="space.100">
					<AbouSectionHeaders>{formatMessage(messages.aboutLabel)}</AbouSectionHeaders>

					<Inline space="space.100" alignBlock="center">
						<SuitcaseIcon label="" color="currentColor" />
						{formatMessage(messages.rovoAgentLabel)}
					</Inline>
				</Stack>
				<Stack space="space.100">
					<AbouSectionHeaders>{formatMessage(messages.knowledgeLabel)}</AbouSectionHeaders>

					<AgentKnowledgeSection
						aris={getEnabledKnowlegeArisFromAgent(agent)}
						isForgeAgent={isForgeAgent}
						knowledgeProductsRender={getKnowledgeProductsRenderForAgent({
							sources: agent.knowledge_sources?.sources ?? [],
							// TODO fill this with the correct value
							cloudUrl: undefined,
							knowledgeScopeEnabled: agent.knowledge_sources?.enabled ?? false,
						})}
					/>
				</Stack>
				<Stack space="space.100">
					<AbouSectionHeaders>{formatMessage(messages.actionLabel)}</AbouSectionHeaders>
					<ActionsSection actions={agent.action_config || []} />
				</Stack>
				<Stack space="space.100">
					<ProfileAboutTeams
						userId={agentIdentityId}
						isActiveUser={!agent.deactivated}
						userFullName={agent.name}
						analyticsSource={AnalyticsEventSource.USER_PROFILE_SCREEN}
					/>
				</Stack>
			</AgentAboutPanelWrapper>
		</>
	);
};

const messages = defineMessages({
	aboutLabel: {
		id: 'ptc-directory.agent-profile.about-agent.about-label',
		defaultMessage: 'About',
		description: 'Label for about section of the agent',
	},
	rovoAgentLabel: {
		id: 'ptc-directory.agent-profile.about-agent.rovo-agent-label',
		defaultMessage: 'Rovo Agent',
		description: 'The content inside About section, indicating this profile is a Rovo Agent',
	},
	knowledgeLabel: {
		id: 'ptc-directory.agent-profile.about-agent.knowledge-label',
		defaultMessage: 'Knowledge',
		description: 'Label for knowledge section of the agent',
	},
	actionLabel: {
		id: 'ptc-directory.agent-profile.about-agent.action-label',
		defaultMessage: 'Actions',
		description: 'Label for action section of the agent',
	},
	teamsLabel: {
		id: 'ptc-directory.agent-profile.about-agent.teams-label',
		defaultMessage: 'Teams',
		description: 'Label for teams section of the agent',
	},
	createTeamLabel: {
		id: 'ptc-directory.agent-profile.about-agent.teams-label',
		defaultMessage: 'Create a team together',
		description: 'Label for teams section of the agent',
	},
});
